/** The packed-atlas contract, and the pure maths for drawing one frame in DOM.
 *
 * `apps/web/public/assets/assets.json` is the authority; this mirrors its shape.
 * Nothing here touches the DOM, so the geometry is testable without a browser.
 */

export interface AtlasFrame {
  readonly sheet: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  /** For a map object this is the ground line, not the box corner. */
  readonly anchorX: number;
  readonly anchorY: number;
  readonly trimX: number;
  readonly trimY: number;
  readonly srcW: number;
  readonly srcH: number;
}

export interface AtlasSheet {
  readonly id: string;
  readonly file: string;
  readonly w: number;
  readonly h: number;
}

export interface AtlasManifest {
  readonly sheets: readonly AtlasSheet[];
  readonly keys: Readonly<Record<string, AtlasFrame>>;
}

export interface Atlas {
  readonly manifest: AtlasManifest;
  /** Absolute or root-relative URL of a sheet image, by sheet id. */
  sheetUrl(sheetId: string): string;
  frame(key: string): AtlasFrame | null;
  has(key: string): boolean;
}

/** A style map, kept as plain strings so this module needs no React types. */
export type StyleMap = Readonly<Record<string, string>>;

/**
 * Largest integer `d` such that `src / d <= target`, i.e. the crispest exact
 * divisor at or below the requested size. Pixel art shrunk by 3.7x shimmers;
 * shrunk by exactly 4x it does not.
 */
export function integerDivisor(src: number, target: number): number {
  if (target <= 0 || src <= 0) return 1;
  if (target >= src) return 1;
  return Math.max(1, Math.ceil(src / target));
}

/** Nearest whole multiple at or below `target`, for drawing *larger* than source. */
export function integerMultiple(src: number, target: number): number {
  if (src <= 0) return 1;
  return Math.max(1, Math.floor(target / src));
}

export interface SpriteBox {
  /** Whole CSS pixels. Never fractional: see `fitSpriteBox`. */
  readonly width: number;
  readonly height: number;
  /** True when the frame is drawn at or above source size. */
  readonly magnified: boolean;
  /**
   * A whole multiple when magnifying, and the reciprocal of a whole divisor
   * when shrinking - unless that would leave the box visibly unfilled, in
   * which case it is the exact fit. See `WHOLE_RATIO_SLACK`.
   */
  readonly scale: number;
}

/**
 * How much of the box a whole-ratio downscale may leave empty, in CSS px.
 *
 * A whole divisor is the crisp answer and it is the answer nearly every time,
 * because at a divisor of 4 or more the next divisor up is only a few pixels
 * away. It stops being the answer when the frame is a *hair* too big for its
 * box: a 225x242 keep in a 236px box is 1.03 too big, so the whole rule drops
 * from 1 to 2 and draws it at 112. Measured on the shipped Hall of Fame, four
 * keeps (Castle, Emberpit, Prismgate, Saltreach) drew at 112px beside eight at 225 -
 * one band, two sizes, and nothing about the art said why. Measured on New
 * Game, a 1280x720 painting in a 250x140 tile divides by 6 to 213x120 and the
 * tile framed it in 18px of black down each side, which is the one thing a
 * picker of paintings must not do.
 *
 * So the rule is: take the whole ratio unless it leaves more than this much of
 * the box bare, and then take the exact fit. 24px is chosen against the three
 * call sites that bracket it, measured at 1920x1080: a 96px icon gives up 6px
 * in a 30px socket and a 256px hero portrait gives up 21px in the adventure
 * sidebar's 106px card, and both of those keep their whole divisor - the
 * portrait especially, because `InfoPanel` sizes the card AROUND a whole
 * divisor of 256 on purpose. The faction tile gives up 37px and the Hall of
 * Fame keep gives up 115, and those two take the exact fit.
 *
 * The exact fit is not a licence to interpolate: every sprite in this kit is
 * `image-rendering: pixelated`, so a fractional downscale drops source rows
 * rather than blending them. Dropping one row in forty is not what a reviewer
 * sees; a sprite at half the size of the one beside it is.
 */
const WHOLE_RATIO_SLACK = 24;

/**
 * Fit a frame inside a `boxW x boxH` rectangle at a whole-number scale ratio.
 *
 * This is the one fitting path in the package. Two things it guarantees, and
 * both of them are load-bearing:
 *
 * 1. **The ratio is whole in both directions, as far as the box allows.**
 *    Upscales are always an exact integer multiple. Downscales are the
 *    reciprocal of an exact integer divisor unless that divisor would leave
 *    more than `WHOLE_RATIO_SLACK` of the box bare, which is the case a whole
 *    ratio cannot serve: a frame a percent too big for its box drops a whole
 *    divisor and draws at half the size of its neighbour in the same band.
 * 2. **The returned box is whole CSS pixels.** Layout hands sockets fractional
 *    widths all the time (`flex: 1 1 0` over seven slots on a 261px panel), and
 *    a `49.6667px` element re-introduces the resample that (1) just removed,
 *    whatever `image-rendering` says. Rounding happens here, before the number
 *    reaches the DOM. It floors, so the drawn box never overruns into the
 *    frame's packing gutter.
 *
 * Fitting by the *box* rather than by the frame's longest side into a square is
 * what makes a socket rack read as one army: a spearwarden frame is 149x293
 * because it carries a pike two feet over his head and an archer's is 148x255
 * and all archer, so the longest-side rule drew one of them a third smaller
 * than his neighbour in the same socket.
 */
export function fitSpriteBox(frame: AtlasFrame, boxW: number, boxH: number): SpriteBox {
  const w = Math.max(1, frame.w);
  const h = Math.max(1, frame.h);
  const bw = Math.max(1, Math.floor(boxW));
  const bh = Math.max(1, Math.floor(boxH));
  // Magnification stays whole with no escape hatch. A fractional UPSCALE
  // doubles some source rows and not others, which is a visible stagger down
  // every edge of the sprite; a fractional downscale only ever drops rows.
  if (bw >= w && bh >= h) {
    const mul = Math.max(1, Math.min(Math.floor(bw / w), Math.floor(bh / h)));
    return { width: w * mul, height: h * mul, magnified: true, scale: mul };
  }
  const exact = Math.min(bw / w, bh / h);
  const div = Math.max(1, Math.ceil(1 / exact));
  const whole = {
    width: Math.max(1, Math.floor(w / div)),
    height: Math.max(1, Math.floor(h / div)),
    magnified: false,
    scale: 1 / div,
  };
  const fit = {
    width: Math.max(1, Math.floor(w * exact)),
    height: Math.max(1, Math.floor(h * exact)),
    magnified: false,
    scale: exact,
  };
  const bare = Math.max(fit.width - whole.width, fit.height - whole.height);
  return bare > WHOLE_RATIO_SLACK ? fit : whole;
}

/** `fitSpriteBox` into a square. Kept because most icons *are* square boxes. */
export function fitSprite(frame: AtlasFrame, target: number): SpriteBox {
  return fitSpriteBox(frame, target, target);
}

/**
 * CSS for one atlas frame as a background, fitted to a `boxW x boxH` rectangle.
 *
 * The sheet is scaled by the same whole ratio as the frame so the sample grid
 * stays aligned. `image-rendering: pixelated` on `.hm-icon` is what stops the
 * compositor inventing colours between the samples; measured in this Chrome, a
 * 4x downscale of a four-colour source keeps four colours with `pixelated` and
 * produces 557 of them with `auto`.
 *
 * ## The box is rounded and the sample grid is NOT, and that is deliberate
 *
 * `backgroundPosition` and `backgroundSize` are left exactly fractional -
 * `-1284.667px` off a `1365.333px` sheet for a war machine at x=3854 divided
 * by three - and every few months somebody reads that as the same defect
 * `fitSpriteBox` fixes for the box and floors it too. It is not, and flooring
 * it is what breaks the sprite.
 *
 * The maths: with size `S = sheet.w / div` and position `P = -frame.x / div`,
 * destination pixel `i` samples source texel `(i + 0.5 - P) * sheet.w / S =
 * i*div + frame.x + div/2` - the centre of its own div-block, for every `i`,
 * with no accumulated error. Floor `S` and the ratio `sheet.w / S` stops being
 * `div`: 4096/1365 is 3.00073, which is harmless at the sheet's origin and a
 * whole texel out by x=3900. That is a scale error, and a scale error walks.
 *
 * Measured rather than reasoned, on this Chrome at `deviceScaleFactor: 1`, by
 * rendering a frame and comparing every pixel against a Pillow nearest-
 * neighbour downsample of the same frame (2026-08-29). Nine frames from four
 * sheets - x from 2 to 3966, div 2 and 3 - each at element offsets 0, 1/3,
 * 1/2 and 3/4 of a pixel, 36 renders per formula:
 *
 *   exact (this function)   0 mismatched pixels in 36 of 36 renders
 *   floor position + size   19% to 51% of pixels wrong wherever div = 3
 *
 * The floored formula is clean only where the divisor divides 4096, i.e. at
 * div 2 and 4; at div 3 it is wrong in proportion to the frame's distance from
 * the sheet origin. `image-rendering: pixelated` does not save it and no test
 * that reads these numbers as strings can see it.
 *
 * What DOES have to be whole is the drawn box, and `fitSpriteBox` floors it -
 * an element 93.5px wide re-introduces a resample whatever the sample grid
 * says. Box: whole. Sample grid: exact. They are different questions.
 */
export function spriteStyleBox(
  atlas: Atlas, key: string, boxW: number, boxH: number,
): StyleMap {
  const bw = Math.max(1, Math.floor(boxW));
  const bh = Math.max(1, Math.floor(boxH));
  const frame = atlas.frame(key);
  if (frame === null) {
    return { width: `${bw}px`, height: `${bh}px` };
  }
  const sheet = atlas.manifest.sheets.find((s) => s.id === frame.sheet);
  const box = fitSpriteBox(frame, bw, bh);
  const k = box.scale;
  const style: Record<string, string> = {
    width: `${box.width}px`,
    height: `${box.height}px`,
    backgroundPosition: `${-frame.x * k}px ${-frame.y * k}px`,
  };
  if (sheet !== undefined) {
    style["backgroundImage"] = `url(${atlas.sheetUrl(sheet.id)})`;
    style["backgroundSize"] = `${sheet.w * k}px ${sheet.h * k}px`;
  }
  return style;
}

/** `spriteStyleBox` into a square. */
export function spriteStyle(atlas: Atlas, key: string, target: number): StyleMap {
  return spriteStyleBox(atlas, key, target, target);
}
